/* eslint-disable react/jsx-key */
import { uniqBy } from 'lodash';
import React from 'react';
import {
  QuoteCoverageGroup,
  QuoteQuality,
  SubCoverage,
  LouiseQuoteWithQuality,
} from '../../../../../../../../../types/Quotes';
import {
  HStack,
  Icon,
  StarRating,
  Text,
  Tooltip,
} from '@wegroup/design-system';
import OfferOptionButton from '../../../../../../components/offers/offer-options/OfferOptionButton';

// Components
import ComparisonMatrixItem from './ComparisonMatrixItem';
import ComparisonMatrixTitle from '../ComparisonMatrixTitle';
import { getCoverageRatingForBase } from '../../../../../../services/productFactoryService';
import { useTranslation } from 'react-i18next';

interface Props {
  quotes: LouiseQuoteWithQuality[];
  option: QuoteQuality;
  last?: boolean;
}

const ComparisonMatrixOptions: React.FC<React.PropsWithChildren<Props>> = ({
  quotes,
  option,
  last,
}) => {
  const { t } = useTranslation();
  const getCoveragesValues = (subOption: QuoteCoverageGroup) =>
    quotes.map(({ quality }) => {
      const foundQuality = quality
        .filter(({ key }) => key === option.key)
        .find(({ coverage_groups }) =>
          coverage_groups.find(({ key }) => subOption.key === key),
        );
      const foundSubOption = foundQuality?.coverage_groups.find(
        ({ key }) => subOption.key === key,
      );
      return (
        <>
          {foundSubOption?.rating ? (
            <StarRating
              starCount={getCoverageRatingForBase(foundSubOption.rating) || 0}
              color="orange"
            />
          ) : foundSubOption?.type === 'INFO' ? (
            <Icon
              name="GeneralStatusWarningTriangle"
              color="warning.main"
              boxSize={8}
            />
          ) : (
            <Icon name="IconActionClose" color="danger.main" boxSize={8} />
          )}
        </>
      );
    });

  const getCoveragesDescriptions = (subOption: QuoteCoverageGroup) => {
    return quotes.map(({ quality }) => {
      const foundQuality = quality
        .filter(({ key }) => key === option.key)
        .find(({ coverage_groups }) =>
          coverage_groups.find(({ key }) => subOption.key === key),
        );
      const foundSubOption = foundQuality?.coverage_groups.find(
        ({ key }) => subOption.key === key,
      );
      if (foundSubOption?.description)
        return (
          <Text
            textStyle="smallBody"
            textAlign="center"
            dangerouslySetInnerHTML={{ __html: foundSubOption.description }}
          />
        );
      return null;
    });
  };

  const getCoveragesSubValues = (subOption: QuoteCoverageGroup) => {
    return subOption.coverages.map(({ name, key: subCoverageKey }) => ({
      title: name,
      values: quotes.map(({ quality }) => {
        let subCoverage: SubCoverage | undefined;
        quality
          .filter(({ key }) => key === option.key)
          .find(({ coverage_groups }) => {
            const coverage = coverage_groups.find(
              ({ key }) => subOption.key === key,
            );
            subCoverage = coverage?.coverages.find(
              ({ key }) => key === subCoverageKey,
            );

            return subCoverage;
          });

        if (subCoverage?.coverage_limit?.type === 'BOOLEAN')
          return subCoverage.coverage_limit.value === 'True' ? (
            <Icon name="GeneralStatusSuccessCheckmark" color="success.main" />
          ) : (
            <Icon name="GeneralActionsCloseOrRemove" color="danger.main" />
          );

        return subCoverage ? subCoverage.coverage_limit.value : null;
      }),
    }));
  };

  const coverages: QuoteCoverageGroup[] = uniqBy(
    ([] as QuoteCoverageGroup[]).concat(
      ...quotes.map(({ quality }) =>
        ([] as QuoteCoverageGroup[]).concat(
          ...quality
            .filter(({ key }) => key === option.key)
            .map(({ coverage_groups }) => coverage_groups),
        ),
      ),
    ),
    'key',
  );
  const bestOfferindex = quotes.findIndex(({ bestOffer }) => bestOffer);

  return (
    <>
      <ComparisonMatrixTitle quotes={quotes}>
        <HStack spacing="2">
          <Text>{option.name}</Text>
          {getCoveragesDescriptions(coverages[0]).length && (
            <Tooltip
              label={t(
                'SALES-CONVERSATION_OFFERS_COMPARISON_CLICKABLE_TOOLTIP',
              )}
              shouldWrapChildren
            >
              <Icon name="GeneralStatusInfoOutline" boxSize="5" />
            </Tooltip>
          )}
        </HStack>
      </ComparisonMatrixTitle>

      {coverages.map((subOption, index) => (
        <ComparisonMatrixItem
          values={{
            nodes: getCoveragesValues(subOption),
            extraNodes: getCoveragesDescriptions(subOption),
          }}
          bestOfferIndex={bestOfferindex}
          last={index === coverages.length - 1 && last}
          {...(subOption.coverages.length > 0 && {
            subValues: getCoveragesSubValues(subOption),
          })}
        >
          <OfferOptionButton
            type={subOption.icon_key}
            active={subOption.is_important_to_customer}
            isSmall
          />
          <Text ml="2">{subOption.name}</Text>
        </ComparisonMatrixItem>
      ))}
    </>
  );
};

export default ComparisonMatrixOptions;
